import React, { useState, useEffect } from 'react';
import { ModelType, ModelSettings, ModelConfiguration } from './types';
import { Button, Select, LoadingSpinner, Modal } from '../../common';
import { useAdminTranslation } from '../../../modules/i18n/hooks/useTranslation';

interface ModelSettingsModalProps {
  isOpen: boolean;
  modelType: ModelType | null;
  config: ModelConfiguration | null;
  onClose: () => void;
  onSave: (modelType: ModelType, settings: ModelSettings) => Promise<void>;
}

const ModelSettingsModal: React.FC<ModelSettingsModalProps> = ({
  isOpen,
  modelType,
  config,
  onClose,
  onSave
}) => {
  const [settings, setSettings] = useState<ModelSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const { t } = useAdminTranslation();

  useEffect(() => {
    if (config) {
      setSettings({ ...config.currentSettings });
    }
  }, [config]);

  if (!isOpen || !modelType || !config || !settings) {
    return null;
  }

  const getModelTypeTitle = (type: ModelType) => {
    return t(`models.settingsTitle.${type}`);
  };

  const handleSave = async () => {
    if (!settings || !modelType) return;

    setSaving(true);
    try {
      await onSave(modelType, settings);
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (key: keyof ModelSettings, value: any) => {
    console.log('Updating settings:', key, value);
    setSettings(prev => {
      if (!prev) return null;
      const newSettings = { ...prev, [key]: value };
      console.log('New settings:', newSettings);
      return newSettings;
    });
  };

  const selectedModel = config.availableModels.find(m => m.id === settings.modelId);

  const footerContent = (
    <div className="flex justify-end gap-3">
      <Button
        variant="secondary"
        onClick={onClose}
        disabled={saving}
      >
        {t('models.cancel')}
      </Button>
      <Button
        variant="primary"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? t('models.saving') : t('models.save')}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getModelTypeTitle(modelType)}
      size="md"
      height="auto"
      footer={footerContent}
    >
      <div className="relative">
        {/* Loading State */}
        {saving && (
          <div className="absolute inset-0 bg-white dark:bg-dark-background-secondary flex items-center justify-center z-10">
            <div className="flex flex-col items-center gap-3">
              <LoadingSpinner />
              <span className="text-sm text-gray-600 dark:text-dark-text-secondary">{t('models.saving')}</span>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Model Selection */}
          <div>
            <label className="block text-sm font-medium text-black dark:text-dark-text mb-2">
              {config?.fallbackSettings ? t('models.primaryModel') : t('models.selectModel')}
            </label>
            <Select
              value={settings.modelId}
              onChange={(value) => {
                const model = config.availableModels.find(m => m.id === value);
                if (model) {
                  updateSettings('modelId', value);
                  updateSettings('modelName', model.name);
                  updateSettings('provider', model.provider);
                }
              }}
              options={config.availableModels.map(model => ({
                value: model.id,
                label: `${model.name} (${model.provider})`
              }))}
              placeholder={t('models.selectModel') + '...'}
            />
            {selectedModel?.description && (
              <p className="mt-1 text-sm text-gray-700 dark:text-dark-text-secondary">
                {selectedModel.description}
              </p>
            )}
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={settings.isActive}
              onChange={(e) => updateSettings('isActive', e.target.checked)}
              className="rounded border-gray-300 text-interactive-primary focus:ring-interactive-primary dark:border-border-dark dark:bg-dark-background-tertiary"
            />
            <span className="ml-2 text-sm text-black dark:text-dark-text">
              {t('models.activate')}
            </span>
          </div>

          {/* Fallback Model Info */}
          {config.fallbackSettings && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800/50 dark:bg-orange-900/20">
              <div className="mb-2 flex items-center gap-2">
                <svg className="h-4 w-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-sm font-medium text-gray-900 dark:text-dark-text">
                  {t('models.fallbackModel')}
                </span>
                <span className="ml-auto inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400">
                  {t('models.autoConfigured')}
                </span>
              </div>
              <div className="text-sm font-semibold text-gray-900 dark:text-dark-text">
                {config.fallbackSettings.modelName}
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-dark-text-secondary">
                Provider: {config.fallbackSettings.provider}
                {config.fallbackSettings.dimensions && ` • Dimensions: ${config.fallbackSettings.dimensions}`}
                {config.fallbackSettings.topK && ` • Top K: ${config.fallbackSettings.topK}`}
                {config.fallbackSettings.temperature !== undefined && ` • Temperature: ${config.fallbackSettings.temperature}`}
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-dark-text-secondary italic">
                {t('models.fallbackDescription')}
              </p>
            </div>
          )}

          {/* Model-specific settings */}
          {modelType === 'llm' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-black dark:text-dark-text mb-2">
                    {t('models.temperature')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="2"
                    step="0.1"
                    value={settings.temperature || 0}
                    onChange={(e) => updateSettings('temperature', parseFloat(e.target.value) || 0 || 0)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-interactive-primary focus:outline-none focus:ring-1 focus:ring-interactive-primary dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black dark:text-dark-text mb-2">
                    {t('models.maxTokens')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="8192"
                    value={settings.maxTokens || 0}
                    onChange={(e) => updateSettings('maxTokens', parseInt(e.target.value) || 0)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-interactive-primary focus:outline-none focus:ring-1 focus:ring-interactive-primary dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-black dark:text-dark-text mb-2">
                    {t('models.topP')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={settings.topP || 0}
                    onChange={(e) => updateSettings('topP', parseFloat(e.target.value) || 0)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-interactive-primary focus:outline-none focus:ring-1 focus:ring-interactive-primary dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black dark:text-dark-text mb-2">
                    {t('models.presencePenalty')}
                  </label>
                  <input
                    type="number"
                    min="-2"
                    max="2"
                    step="0.1"
                    value={settings.presencePenalty || 0}
                    onChange={(e) => updateSettings('presencePenalty', parseFloat(e.target.value) || 0)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-interactive-primary focus:outline-none focus:ring-1 focus:ring-interactive-primary dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black dark:text-dark-text mb-2">
                    {t('models.frequencyPenalty')}
                  </label>
                  <input
                    type="number"
                    min="-2"
                    max="2"
                    step="0.1"
                    value={settings.frequencyPenalty || 0}
                    onChange={(e) => updateSettings('frequencyPenalty', parseFloat(e.target.value) || 0)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-interactive-primary focus:outline-none focus:ring-1 focus:ring-interactive-primary dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text"
                  />
                </div>
              </div>
            </>
          )}

          {modelType === 'embedding' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-black dark:text-dark-text mb-2">
                  {t('models.dimensions')}
                </label>
                <input
                  type="number"
                  min="1"
                  value={settings.dimensions || 1536}
                  onChange={(e) => updateSettings('dimensions', parseInt(e.target.value) || 0)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-interactive-primary focus:outline-none focus:ring-1 focus:ring-interactive-primary dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-black dark:text-dark-text mb-2">
                  {t('models.batchSize')}
                </label>
                <input
                  type="number"
                  min="1"
                  value={settings.batchSize || 100}
                  onChange={(e) => updateSettings('batchSize', parseInt(e.target.value) || 0)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-interactive-primary focus:outline-none focus:ring-1 focus:ring-interactive-primary dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text"
                />
              </div>
            </div>
          )}

          {modelType === 'ranking' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-black dark:text-dark-text mb-2">
                  {t('models.topK')}
                </label>
                <input
                  type="number"
                  min="1"
                  value={settings.topK || 10}
                  onChange={(e) => updateSettings('topK', parseInt(e.target.value) || 0)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-interactive-primary focus:outline-none focus:ring-1 focus:ring-interactive-primary dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-black dark:text-dark-text mb-2">
                  {t('models.threshold')}
                </label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  value={settings.threshold || 0.5}
                  onChange={(e) => updateSettings('threshold', parseFloat(e.target.value) || 0)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-interactive-primary focus:outline-none focus:ring-1 focus:ring-interactive-primary dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ModelSettingsModal;